import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  debounce
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { UserContext } from "../../../../Context/UserContext";
import { Center } from "../../../../Models/Center";
import {
  CenterMessage,
  CenterMessageCenter,
  EnhancedCenterMessage,
  EnhancedCenterMessageCenter
} from "../../../../Models/CenterMessage";
import { DynamicContentTab, DynamicContentView } from "../../../../Types/DynamicContentView";
import { Error } from "../../../../Utils/Errors";
import { useApi } from "../../../../Utils/useApi";
import DynamicContentPanel from "../../../DynamicContentPanel/DynamicContentPanel";
import AdminReadmeFormLabel from "../../AdminReadmeFormLabel";
import ToolWrapper from "../ToolWrapper";
import { CenterMessagesReadme } from "./CenterMessages.Readme";

const CenterMessages: React.FC = () => {
  const { dynamicContent } = useContext(UserContext);
  const [dynamicContentView, setDynamicContentView] = useState(
    () => dynamicContent && new DynamicContentView(dynamicContent)
  );

  const [previewDate, setPreviewDate] = useState<DateTime>(DateTime.now());
  const [timeRange, setTimeRange] = useState<{ start: DateTime; end: DateTime }>({
    start: DateTime.now().minus({ days: 30 }),
    end: DateTime.now().plus({ days: 30 })
  });
  const [centerMessages, setCenterMessages] = useState<EnhancedCenterMessageCenter[]>([]);
  const [selectedCmc, setSelectedCmc] = useState<EnhancedCenterMessageCenter>();
  const [selectedMessage, setSelectedMessage] = useState<EnhancedCenterMessage>();
  const [updateMessage, setUpdateMessage] = useState<EnhancedCenterMessage>();
  const [isEditing, setIsEditing] = useState(false);
  const [messageMarkdown, setMessageMarkdown] = useState("");
  const [messageTitle, setMessageTitle] = useState("");

  useEffect(() => {
    if (!dynamicContent) return;
    setDynamicContentView(new DynamicContentView(dynamicContent));
  }, [dynamicContent]);

  // update dynamic content preview
  useEffect(() => {
    let previewMessages: EnhancedCenterMessage[] | undefined;
    if (selectedCmc) {
      previewMessages = selectedCmc.messages.map(m => ({ ...m }));
      if (selectedMessage?.id) {
        const index = selectedCmc.messages.findIndex(m => m.id === selectedMessage.id);
        if (index !== -1) previewMessages[index] = { ...selectedMessage };
        else previewMessages.unshift(selectedMessage);
      } else if (selectedMessage) previewMessages.unshift(selectedMessage);
    } else if (selectedMessage) previewMessages = [selectedMessage];
    const tabs = (dynamicContent || []).map(dc => new DynamicContentTab(dc));
    const centerMessages = previewMessages && EnhancedCenterMessage.filterMessages(previewMessages, previewDate);
    setDynamicContentView(oldView => {
      if (!oldView) return oldView;
      const centerTabExists = oldView.tabs[0].title.includes("Center");
      if (centerMessages?.length) {
        const newTab = centerTabExists
          ? {
              ...oldView.tabs[0],
              title: `Center (${centerMessages.length})`,
              content: EnhancedCenterMessage.buildCentersContent(centerMessages)
            }
          : EnhancedCenterMessage.buildCentersContentTab(centerMessages);
        if (selectedMessage?.id) newTab.showChildIndex = newTab.content.findIndex(c => c.id === selectedMessage.id);
        tabs.unshift(newTab);
      } else if (centerTabExists) oldView.tabs.splice(0, 1);
      return { ...oldView, tabs };
    });
  }, [selectedCmc, selectedMessage, dynamicContent, previewDate]);

  // #region GET Center Messages
  const { response: getMessages, isLoading: getMessagesLoading } = useApi<CenterMessageCenter[]>({
    method: "get",
    url: "Legacy/V0/centers/messages",
    config: {
      params: { startDate: timeRange.start.toISODate(), endDate: timeRange.end.toISODate() }
    },
    error: Error.GetCentersMessages
  });
  useEffect(() => {
    if (getMessages)
      setCenterMessages(
        getMessages.map(cm => ({
          ...cm,
          messages: cm.messages.map(m => new EnhancedCenterMessage(m))
        }))
      );
  }, [getMessages]);
  // #endregion

  // #region Update or Add Center Message
  const { response: upsertMessage, isLoading: upsertMessageLoading } = useApi<CenterMessage, CenterMessage>({
    method: updateMessage?.id ? "put" : "post",
    url: `Legacy/V0/Centers/Messages${updateMessage?.id ? `/${updateMessage.id}` : ""}`,
    data: updateMessage,
    valid: Boolean(updateMessage),
    error: updateMessage?.id ? Error.PutCenterMessage : Error.PostCenterMessage
  });
  useEffect(() => {
    if (upsertMessage) {
      const enhanced = new EnhancedCenterMessage(upsertMessage);
      setCenterMessages(oldMessages => {
        let isUpdate = false;
        const newMessages = oldMessages.map(message => {
          if (!enhanced.centers.some(c => c.id === message.center.id)) return message;
          const index = message.messages.findIndex(m => m.id === enhanced.id);
          if (index !== -1) {
            message.messages[index] = enhanced;
            isUpdate = true;
          } else message.messages.push(enhanced);
          return { ...message, messages: [...message.messages] };
        });
        toast.success(`${isUpdate ? "Updated" : "Created"} the center message.`);
        return newMessages;
      });
      setIsEditing(false);
    }
  }, [upsertMessage]);
  // #endregion

  // #region Actions
  const toggleSelectedCenter = (center: Center) =>
    setSelectedMessage(oldSelectedMessage => {
      if (!oldSelectedMessage) return oldSelectedMessage;
      const index = oldSelectedMessage.centers.findIndex(c => c.id === center.id);
      if (index === -1) oldSelectedMessage.centers.push(center);
      else oldSelectedMessage.centers.splice(index, 1);
      return { ...oldSelectedMessage };
    });
  const debouncedUpdateSelectedMessageMessage = useCallback(
    debounce((message: string) => setSelectedMessage(m => (m ? { ...m, message } : m)), 500),
    []
  );
  const debouncedUpdateSelectedMessageTitle = useCallback(
    debounce((title: string) => setSelectedMessage(m => (m ? { ...m, title } : m)), 500),
    []
  );
  const onTitleChange = useCallback((value: string) => {
    setMessageTitle(value);
    debouncedUpdateSelectedMessageTitle(value);
  }, []);
  const onSelectCenter = (message: EnhancedCenterMessageCenter) => {
    setSelectedCmc(message);
    setSelectedMessage(undefined);
    setMessageMarkdown("");
  };
  const onMarkdownChange = useCallback((value: string) => {
    setMessageMarkdown(value);
    debouncedUpdateSelectedMessageMessage(value);
  }, []);
  const onMessageClick = useCallback((message: EnhancedCenterMessage) => {
    setSelectedMessage({ ...message });
    setMessageTitle(message.title);
    setMessageMarkdown(message.message);
  }, []);
  const onTimeSpanChange = (start?: DateTime, end?: DateTime) => {
    if (!start || !end) return;
    setTimeRange({ start, end });
    // if our preview date is already within the new range, do nothing
    if (previewDate >= start && previewDate <= end) return;
    // otherwise, update the preview to the range bound that is closest to today
  };
  const onNewMessage = () => {
    const startDate = DateTime.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    setSelectedCmc(undefined);
    setSelectedMessage({
      message: "",
      title: "",
      startDate,
      startDateUtc: startDate.toISO(),
      centers: selectedCmc?.center ? [selectedCmc.center] : []
    });
    setIsEditing(true);
  };
  const onCancel = () => {
    setMessageMarkdown("");
    setSelectedMessage(undefined);
    setIsEditing(false);
  };
  //#endregion

  return (
    <ToolWrapper
      title="Center Messages"
      readme={CenterMessagesReadme.tool}
      isLoading={getMessagesLoading || !dynamicContentView}
    >
      <Grid container wrap="nowrap" columnSpacing={4} sx={{ width: "100%", height: "100%", overflow: "hidden", p: 2 }}>
        <Grid item container xs={4} direction="column" wrap="nowrap" rowSpacing={2}>
          <Grid item>
            <AdminReadmeFormLabel
              label="Messages Active For Time Span"
              readme={CenterMessagesReadme.queryMessagesTimespan}
            />
          </Grid>
          <Grid item>
            <DateTimePicker
              renderInput={props => <TextField {...props} />}
              label="Start Time"
              value={timeRange.start}
              onChange={newValue => newValue && onTimeSpanChange(newValue, timeRange.end)}
            />
          </Grid>
          <Grid item>
            <DateTimePicker
              componentsProps={{ actionBar: { actions: ["clear"] } }}
              renderInput={props => <TextField {...props} />}
              label="End Time"
              value={timeRange.end}
              onChange={newValue => onTimeSpanChange(timeRange.start, newValue || undefined)}
            />
          </Grid>
          <Grid item xs style={{ overflow: "auto" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Center</TableCell>
                  <TableCell align="right">
                    {isEditing ? `${selectedMessage?.centers.length} Selected` : "Messages (Live/All)"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {centerMessages.map(centerMessage => {
                  const liveMessages = EnhancedCenterMessage.filterMessages(centerMessage.messages, previewDate);
                  return (
                    <React.Fragment key={centerMessage.center.id}>
                      <TableRow
                        selected={
                          isEditing
                            ? selectedMessage?.centers.some(c => c.id === centerMessage.center.id)
                            : centerMessage.center.id === selectedCmc?.center.id
                        }
                        onClick={() =>
                          isEditing
                            ? !selectedMessage?.id && toggleSelectedCenter(centerMessage.center)
                            : onSelectCenter(centerMessage)
                        }
                      >
                        <TableCell>{centerMessage.center.name}</TableCell>
                        <TableCell align="right">
                          {!isEditing && `${liveMessages.length}/${centerMessage.messages.length}`}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse
                            in={!isEditing && centerMessage.center.id === selectedCmc?.center.id}
                            timeout="auto"
                            unmountOnExit
                          >
                            <Box margin={1}>
                              {centerMessage.messages.length ? (
                                <Table size="small" padding="none">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Title</TableCell>
                                      <TableCell>Start Date</TableCell>
                                      <TableCell>End Date</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {centerMessage.messages.map((message, index) => (
                                      <TableRow
                                        hover
                                        onClick={() => onMessageClick(message)}
                                        selected={message.id === selectedMessage?.id}
                                        key={index}
                                      >
                                        <TableCell>{message.title}</TableCell>
                                        <TableCell>{message.startDate.toLocaleString(DateTime.DATETIME_MED)}</TableCell>
                                        <TableCell>
                                          {message.endDate?.toLocaleString(DateTime.DATETIME_MED) || ""}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography variant="body2" gutterBottom component="div">
                                  <strong>No Messages</strong>
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Grid>
        </Grid>
        <Grid item container direction="column" justifyContent="space-between" wrap="nowrap" xs>
          <Grid item container direction="column" xs rowSpacing={1}>
            <Grid item>
              <AdminReadmeFormLabel label="Valid Time" readme={CenterMessagesReadme.validDuration} />
            </Grid>
            <Grid item>
              <DateTimePicker
                renderInput={props => <TextField {...props} />}
                label="Start Time"
                disabled={!isEditing || Boolean(selectedMessage?.id && selectedMessage.startDate < DateTime.now())}
                value={selectedMessage?.startDate || null}
                onChange={startDate =>
                  setSelectedMessage(sm =>
                    startDate && sm ? { ...sm, startDate, startDateUtc: startDate.toUTC().toISO() } : sm
                  )
                }
              />
            </Grid>
            <Grid item>
              <DateTimePicker
                componentsProps={{ actionBar: { actions: ["clear"] } }}
                renderInput={props => <TextField {...props} />}
                label="End Time"
                value={selectedMessage?.endDate || null}
                disabled={!isEditing}
                onChange={endDate =>
                  setSelectedMessage(sm =>
                    sm
                      ? { ...sm, endDate: endDate || undefined, endDateUtc: endDate?.toUTC().toISO() || undefined }
                      : sm
                  )
                }
              />
            </Grid>
            <Grid item>
              <AdminReadmeFormLabel label="Title" readme={CenterMessagesReadme.title} />
            </Grid>
            <Grid item>
              <TextField
                type="text"
                disabled={!isEditing}
                value={messageTitle}
                onChange={event => onTitleChange(event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item>
              <AdminReadmeFormLabel label="Message" readme={CenterMessagesReadme.message} />
            </Grid>
            <Grid item xs style={{ overflow: "auto" }}>
              <TextField
                type="text"
                minRows={6}
                value={messageMarkdown}
                onChange={event => isEditing && onMarkdownChange(event.target.value)}
                multiline
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid item container justifyContent="space-around">
            {isEditing ? (
              <>
                <Grid item>
                  <Button onClick={onCancel} disabled={upsertMessageLoading} color="secondary">
                    Cancel
                  </Button>
                </Grid>
                <Grid item>
                  {upsertMessageLoading ? (
                    <CircularProgress size="2rem" />
                  ) : (
                    <Button
                      disabled={!selectedMessage?.centers.length || !selectedMessage.message}
                      onClick={() => selectedMessage && setUpdateMessage({ ...selectedMessage })}
                    >
                      Save Message
                    </Button>
                  )}
                </Grid>
              </>
            ) : (
              <>
                <Grid item>
                  <Button
                    onClick={() => selectedMessage && setIsEditing(s => !s)}
                    disabled={!selectedMessage || (selectedMessage.endDate && selectedMessage.endDate < DateTime.now())}
                  >
                    Edit Message
                  </Button>
                </Grid>
                <Grid item>
                  <Button onClick={onNewMessage}>New Message</Button>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        <Grid item container xs={4} direction="column" wrap="nowrap" rowSpacing={2}>
          <Grid item>
            <AdminReadmeFormLabel label="Preview" readme={CenterMessagesReadme.preview} />
          </Grid>
          <Grid item>
            <DateTimePicker
              componentsProps={{ actionBar: { actions: ["clear"] } }}
              renderInput={props => <TextField {...props} />}
              label="Preview Time"
              value={previewDate}
              onChange={newValue => newValue && setPreviewDate(newValue)}
            />
          </Grid>
          <Grid item xs style={{ overflow: "auto" }}>
            {dynamicContentView && (
              <DynamicContentPanel
                dynamicContentView={dynamicContentView}
                setDynamicContentView={setDynamicContentView}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </ToolWrapper>
  );
};

export default CenterMessages;
